import { Landscape } from '@/components/generals/landscape'
import { Eyebrow } from '@/components/generals/eyebrow'
import { PillButton } from '@/components/generals/pill-button'
import { Reveal } from '@/components/generals/reveal'
import { SplitHeading } from '@/components/generals/split-heading'

interface ResearchRow {
    label: string
    value: string
    good?: boolean
}

interface ResearchCardProps {
    kind: string
    name: string
    rows: Array<ResearchRow>
    verdict: string
}

const ResearchCard = ({ kind, name, rows, verdict }: ResearchCardProps) => {
    return (
        <div className='glass-dark grain relative w-[260px] overflow-hidden rounded-[24px] p-5 text-left'>
            <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

            <p className='flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.28em] text-cream/50'>
                <span aria-hidden='true' className='size-1 rounded-full bg-citron' />
                {kind}
            </p>

            <p className='mt-2 font-serif text-xl italic text-cream'>
                {name}
            </p>

            <dl className='mt-4 space-y-2.5'>
                {rows.map(row => (
                    <div key={row.label} className='flex items-center justify-between gap-4 text-[13px]'>
                        <dt className='text-cream/55'>
                            {row.label}
                        </dt>

                        <dd className={row.good ? 'text-citron' : 'text-cream'}>
                            {row.value}
                        </dd>
                    </div>
                ))}
            </dl>

            <p className='mt-4 rounded-[14px] bg-white/[0.06] px-3 py-2.5 text-[12px] leading-snug text-cream/75'>
                {verdict}
            </p>
        </div>
    )
}

/** Cinematic wildflower band that introduces the research side of the web app. */
export const DueDiligence = () => {
    return (
        <section id='due-diligence' aria-labelledby='due-diligence-title' className='relative isolate overflow-hidden bg-black'>
            <div aria-hidden='true' className='absolute inset-0'>
                <Landscape src={'/images/uploads/replace-a.png'} fade='top-bottom' position='center 72%' imageClassName='brightness-[.62]' />
                <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.7)_0%,rgba(0,0,0,0.25)_38%,rgba(0,0,0,0.15)_62%,rgba(0,0,0,0.85)_100%)]' />
                <div className='motion-haze absolute inset-0 bg-[radial-gradient(45%_38%_at_50%_42%,rgba(246,200,150,0.22),rgba(246,200,150,0)_70%)] mix-blend-screen' />
            </div>

            <div className='shell relative z-10 flex min-h-[720px] flex-col items-center justify-center py-32 text-center sm:min-h-[860px]'>
                <Eyebrow>
                    In the web app
                </Eyebrow>

                <SplitHeading
                    id='due-diligence-title'
                    text={'Look closely\n*before you invest*'}
                    className='mt-7 max-w-4xl text-balance text-[2.6rem] font-normal leading-[1] tracking-[-0.045em] text-cream drop-shadow-[0_8px_40px_rgba(0,0,0,0.5)] sm:text-6xl md:text-7xl'
                />

                <Reveal as='p' delay={450} className='mt-6 max-w-lg text-pretty text-[15px] leading-relaxed text-cream/70 sm:text-base'>
                    Whether it is a broad index fund or a local company you admire, read a clear, plain-language profile of it before a single dollar leaves your account.
                </Reveal>

                <Reveal delay={700} className='mt-10'>
                    <PillButton href='/account' size='md'>
                        Open the web app
                    </PillButton>
                </Reveal>
            </div>

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 z-10 hidden xl:block'>
                <div className='shell h-full'>
                    <div className='relative h-full'>
                        <Reveal x={-40} y={0} blur={8} delay={500} className='absolute bottom-[16%] left-0'>
                            <div className='motion-float [animation-duration:9s]'>
                                <ResearchCard
                                    kind='Fund review'
                                    name='Northwind Total Market'
                                    rows={[
                                        { label: 'Yearly cost', value: '0.04%', good: true },
                                        { label: 'Already in your 401(k)', value: '62%' },
                                        { label: 'Risk level', value: 'Medium' }
                                    ]}
                                    verdict='Cheap to hold, but much of it doubles up on what you have.'
                                />
                            </div>
                        </Reveal>

                        <Reveal x={40} y={0} blur={8} delay={700} className='absolute right-0 top-[18%]'>
                            <div className='motion-float [animation-delay:2.2s] [animation-duration:10s]'>
                                <ResearchCard
                                    kind='Company profile'
                                    name='Halden Coffee Co.'
                                    rows={[
                                        { label: 'Sales growth this year', value: '+18%', good: true },
                                        { label: 'Borrowing level', value: 'Light', good: true },
                                        { label: 'Weight in your portfolio', value: '1.2%' }
                                    ]}
                                    verdict='Healthy, steady growth. A modest holding suits your goals.'
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    )
}
